import React, { useEffect, useState } from "react";
import { useCallback } from "react";
import { Button } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BackButton from "../../../components/Form/BackButton";
import { API, defaultLimitPagination, get } from "../../../config";
import Pagination from "../../common/Pagination";
import Spin from "../../common/Spin";
import { debounce } from "lodash";
import SuburbsActionButton from "./components/SuburbsActionButton";
import AddSuburbs from "./AddSuburbs";
import { useStateList } from "./components/SuburbsHelper";
import Select from "react-select";
import SearchBox from "../../common/SearchBox";

const Suburbs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [suburbsList, setSuburbsList] = useState([]);
  const [suburbsLoading, setSuburbsLoading] = useState(false);
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const defaultStateId = query.get("state_id");
  const [stateId, setStateId] = useState(defaultStateId || "");

  const [showSuburbs, setShowSuburbs] = useState(false);

  // *API Call for Geting Suburbs - Start
  const fetchSuburbs = useCallback(
    async (passLimit, passPage) => {
      try {
        setSuburbsLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_SUBURB_LIST}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&state_id=${stateId ? stateId : ""}`
        );
        setSuburbsLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const holidayList = data?.data?.data;
        setSuburbsList(holidayList);
        return 0;
      } catch (error) {
        setSuburbsLoading(false);
        setSuburbsList([]);

        if (error?.response?.data?.errors) {
          Object.keys(error?.response?.data?.errors).forEach((key) => {
            toast.error(error?.response?.data?.errors[key][0]);
          });
        } else {
          toast.error(error?.message);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, stateId]
  );

  const suburbsModal = () => {
    fetchSuburbs(limit, page);
  };

  useEffect(() => {
    fetchSuburbs();
  }, [fetchSuburbs]);
  // *API Call for Geting Suburbs - End

  // *Suburbs Table Row and Column Generation, Filtering and Sorting of Record - Start
  const suburbsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: suburbsList[i]?.id,
        id: suburbsList[i]?.id ? suburbsList[i]?.id : "",
        state: suburbsList[i]?.state?.name
          ? suburbsList[i]?.state?.name +
            `${
              suburbsList[i]?.state?.short_name
                ? " (" + suburbsList[i]?.state?.short_name + ")"
                : ""
            }`
          : "-",
        name: suburbsList[i]?.name ? suburbsList[i]?.name : "",
        action: (
          <SuburbsActionButton
            id={suburbsList[i]?.id}
            suburbsModal={suburbsModal}
          />
        ),
      });
    }
    return items;
  };
  const suburbsData = suburbsGenerator(suburbsList?.length);

  const columns = [
    {
      dataField: "name",
      text: "Name",
      sort: true,
    },
    {
      dataField: "state",
      text: "State",
      sort: true,
    },
    {
      dataField: "action",
      text: "Action",
      className: "text-center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  // const onSearchChange = (e) => {
  //   const value = e.target.value;
  //   setSearchParam(value);
  //   setPage(1);
  // };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchSuburbs(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchSuburbs(limit, page);
  };
  // *Suburbs Table Row and Column Generation, Filtering and Sorting of Record - End

  useEffect(() => {
    navigate(
      `/admin/suburbs?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}&state_id=${
        stateId ? stateId : ""
      }`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder, stateId]);

  const toggleSuburbsModal = () => {
    showSuburbs && suburbsModal();
    setShowSuburbs(!showSuburbs);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    if (nextValue?.length >= 2 || nextValue?.length === 0) {
      debouncedSave(nextValue);
    }
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  // State
  const { stateLoading, stateList = [] } = useStateList();
  const stateListing = stateList?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name + ` (${short_name})`, value, ...rest };
  });

  const state = stateListing?.find((item) => item?.value === defaultStateId);

  return (
    <>
      {" "}
      <div className="cms-page">
        <div className="page-content-block">
          <div className="full-content-block">
            <h1 className="page-title">
              <BackButton />
              Suburbs
            </h1>
            <div className="content-details">
              <div className="table-top-btn">
                <SearchBox
                  placeholder={"Search Suburbs"}
                  searchParamData={searchParamData}
                  handleSearchChange={handleSearchChange}
                  handleClearSearch={handleClearSearch}
                />
                <div className="form-group refund-card-content state-filter">
                  <Select
                    name="state_id"
                    value={
                      defaultStateId && state ? state : stateId ? stateId : ""
                    }
                    onChange={(e) => {
                      setStateId(e ? e?.value : "");
                    }}
                    options={stateListing}
                    placeholder="Select State"
                    classNamePrefix="selectbox"
                    isClearable
                    isLoading={stateLoading}
                    autoComplete="off"
                    onKeyPress={(e) => {
                      e.which === 13 && e.preventDefault();
                    }}
                  />
                </div>
                <div className="table-btn">
                  <Button variant="primary" onClick={toggleSuburbsModal}>
                    Add New
                  </Button>
                </div>
              </div>
              <div className="custom-table">
                {suburbsLoading && <Spin />}
                <BootstrapTable
                  keyField="suburbs_id"
                  remote
                  data={suburbsData}
                  columns={columns}
                  onTableChange={handleTableChange}
                  noDataIndication="No Data Found"
                />
                <Pagination
                  total={total}
                  limit={parseInt(limit)}
                  currentPage={page}
                  updateLimit={handleUpdateLimit}
                  updatePage={handleChangePage}
                  from={from}
                  to={to}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSuburbs && (
        <AddSuburbs
          toggleSuburbsModal={toggleSuburbsModal}
          suburbsModal={suburbsModal}
          showSuburbs={showSuburbs}
        />
      )}
    </>
  );
};

export default Suburbs;
